import React, { useState, useEffect } from 'react';
import { FiMail, FiCheckCircle, FiXCircle, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (email) {
      checkUnsubscribeStatus();
      trackPageView();
    }
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackPageView = () => {
    if (email) {
      const img = new Image();
      img.src = `/api/unsubscribe/track/${encodeURIComponent(email)}`;
      img.style.display = 'none';
      document.body.appendChild(img);
    }
  };

  const checkUnsubscribeStatus = async () => {
    try {
      const response = await axios.get(`/api/unsubscribe/check/${email}`);
      setUnsubscribed(response.data.unsubscribed);
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const handleUnsubscribe = async () => {
    if (!email) {
      toast.error('No email address provided');
      return;
    }
    setLoading(true);
    try {
      await axios.post('/api/unsubscribe', { email });
      setUnsubscribed(true);
      toast.success('Successfully unsubscribed');
    } catch (error) {
      toast.error('Error unsubscribing: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-large p-8">

          {/* Icon + Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-2xl mb-4">
              <FiMail className="w-8 h-8 text-neutral-600" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-1">Email Preferences</h1>
            {email && (
              <p className="text-sm text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5 inline-block mt-1">
                {email}
              </p>
            )}
          </div>

          {unsubscribed ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-success-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FiCheckCircle className="w-8 h-8 text-success-600" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 mb-2">You're Unsubscribed</h2>
              <p className="text-neutral-500 text-sm mb-6 leading-relaxed">
                You have successfully unsubscribed from our mailing list. You will no longer receive emails from us.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold text-sm"
              >
                <FiArrowLeft className="w-4 h-4" />
                Return to Home
              </a>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FiXCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 mb-2">Unsubscribe from Emails</h2>
              <p className="text-neutral-500 text-sm mb-6 leading-relaxed">
                We're sorry to see you go. If you unsubscribe, you will no longer receive emails from us.
              </p>
              <button
                onClick={handleUnsubscribe}
                disabled={loading || !email}
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              >
                {loading ? 'Processing...' : 'Confirm Unsubscribe'}
              </button>
              <a
                href="/"
                className="block text-sm text-neutral-500 hover:text-neutral-800 transition-colors"
              >
                Cancel — keep me subscribed
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
